import path from "node:path";

import type { BrowserSource } from "../types/types";

export const CHROMIUM_PATH_ENV = "REACT_PDF_ENGINE_CHROMIUM_PATH";

export type BrowserPathCandidate = {
  name: string;
  source: BrowserSource;
  executablePath: string;
};

const windowsPath = path.win32;

function compact(values: Array<string | undefined>): string[] {
  return values.filter((value): value is string => Boolean(value));
}

function uniqueCandidates(candidates: BrowserPathCandidate[]): BrowserPathCandidate[] {
  const seen = new Set<string>();
  const unique: BrowserPathCandidate[] = [];

  for (const candidate of candidates) {
    const key = windowsPath.normalize(candidate.executablePath).toLowerCase();
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    unique.push(candidate);
  }

  return unique;
}

export function getWindowsBrowserCandidates(
  env: NodeJS.ProcessEnv = process.env
): BrowserPathCandidate[] {
  const programFiles = compact([env.ProgramFiles, env["ProgramFiles(x86)"]]);
  const localAppData = env.LOCALAPPDATA;

  const chromePaths = [
    ...programFiles.map((root) =>
      windowsPath.join(root, "Google", "Chrome", "Application", "chrome.exe")
    ),
    localAppData
      ? windowsPath.join(localAppData, "Google", "Chrome", "Application", "chrome.exe")
      : undefined
  ];

  const edgePaths = [
    ...programFiles.map((root) =>
      windowsPath.join(root, "Microsoft", "Edge", "Application", "msedge.exe")
    ),
    localAppData
      ? windowsPath.join(localAppData, "Microsoft", "Edge", "Application", "msedge.exe")
      : undefined
  ];

  return uniqueCandidates([
    ...compact(chromePaths).map((executablePath) => ({
      name: "Google Chrome",
      source: "chrome" as const,
      executablePath
    })),
    ...compact(edgePaths).map((executablePath) => ({
      name: "Microsoft Edge",
      source: "edge" as const,
      executablePath
    }))
  ]);
}

export function getBundledChromiumCandidates(
  env: NodeJS.ProcessEnv = process.env,
  cwd = process.cwd(),
  processExecPath = process.execPath
): BrowserPathCandidate[] {
  const executableName = process.platform === "win32" ? "chrome.exe" : "chrome";
  const roots = compact([
    cwd,
    path.dirname(processExecPath),
    process.env.APPDIR,
    process.env.PORTABLE_EXECUTABLE_DIR
  ]);

  const relativePaths = [
    ["chromium", executableName],
    ["chromium", "chrome-win", executableName],
    ["chrome-win", executableName],
    ["bin", "chromium", executableName],
    ["resources", "chromium", executableName],
    ["resources", "chrome-win", executableName]
  ];

  const envCandidate = env[CHROMIUM_PATH_ENV]
    ? [
        {
          name: "Bundled Chromium",
          source: "bundled" as const,
          executablePath: env[CHROMIUM_PATH_ENV]
        }
      ]
    : [];

  const conventionCandidates = roots.flatMap((root) =>
    relativePaths.map((segments) => ({
      name: "Bundled Chromium",
      source: "bundled" as const,
      executablePath: path.join(root, ...segments)
    }))
  );

  return uniqueCandidates([...envCandidate, ...conventionCandidates]);
}
